// ModelForm - Create/Edit item model with specifications
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import SubHeaders from '@/components/Headers/SubHeaders'
import { paths } from '@/utils/path'
import { Button, FormItem } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Plus, Trash2 } from 'lucide-react'
import { getItemModel, createItemModel, updateItemModel } from '../../services'
import { CreateModelRequest, SpecificationRequest } from '../../types/request'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

// Category options based on item type (synced with database)
const equipmentCategories = [
    { value: 'server', label: 'Server' },
    { value: 'switch', label: 'Switch' },
    { value: 'laptop', label: 'Laptop' },
]

const componentCategories = [
    { value: 'cpu', label: 'Processor (CPU)' },
    { value: 'ram', label: 'Memory (RAM)' },
    { value: 'hdd', label: 'HDD' },
    { value: 'ssd', label: 'SSD' },
    { value: 'nvme', label: 'NVMe' },
    { value: 'sas', label: 'SAS' },
    { value: 'card_network', label: 'Network Card' },
    { value: 'card_gpu', label: 'GPU Card' },
    { value: 'power_supply', label: 'Power Supply' },
    { value: 'cable', label: 'Cable' },
    { value: 'module', label: 'Module' },
    { value: 'rail', label: 'Rail' },
    { value: 'tray', label: 'Tray' },
]

interface SpecFormData extends Omit<SpecificationRequest, 'display_order'> {
    tempId: string
}

interface ModelFormData {
    item_type: 'equipment' | 'component'
    category: string
    brand: string
    model: string
    name: string
    capacity: string
    description: string
    is_active: boolean
}

const ModelForm = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const isEditMode = Boolean(id)

    const [specifications, setSpecifications] = useState<SpecFormData[]>([])

    const methods = useForm<ModelFormData>({
        defaultValues: {
            item_type: 'equipment',
            category: '',
            brand: '',
            model: '',
            name: '',
            capacity: '',
            description: '',
            is_active: true,
        },
    })

    const { control, handleSubmit, reset, watch, setValue } = methods
    const watchedItemType = watch('item_type')
    const watchedIsActive = watch('is_active')

    // Fetch model data for edit mode
    const { data: modelData, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_MODELS, id],
        queryFn: () => getItemModel(id!),
        enabled: isEditMode,
    })

    useEffect(() => {
        if (modelData) {
            reset({
                item_type: modelData.item_type,
                category: modelData.category,
                brand: modelData.brand || '',
                model: modelData.model,
                name: modelData.name,
                capacity: modelData.capacity || '',
                description: modelData.description || '',
                is_active: modelData.is_active,
            })
            setSpecifications(
                modelData.specifications?.map((spec) => ({
                    tempId: spec.id || crypto.randomUUID(),
                    id: spec.id,
                    name: spec.name,
                    value: spec.value,
                    unit: spec.unit || '',
                    is_primary: spec.is_primary,
                })) || []
            )
        }
    }, [modelData, reset])

    // Create mutation
    const { mutateAsync: submitCreate, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: (data: CreateModelRequest) => createItemModel(data),
            },
            {
                successMessage: 'Model created successfully',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_MODELS,
                onSuccess: () => {
                    navigate(paths.warehouseModels)
                },
            }
        )

    // Update mutation
    const { mutateAsync: submitUpdate, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: (data: CreateModelRequest) =>
                    updateItemModel(id!, data),
            },
            {
                successMessage: 'Model updated successfully',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_MODELS,
                onSuccess: () => {
                    navigate(paths.warehouseModels)
                },
            }
        )

    const isPending = isCreating || isUpdating

    const onSubmit = async (data: ModelFormData) => {
        const payload: CreateModelRequest = {
            item_type: data.item_type,
            category: data.category,
            brand: data.brand || undefined,
            model: data.model,
            name: data.name,
            capacity: data.capacity || undefined,
            description: data.description || undefined,
            is_active: data.is_active,
            specifications: specifications.map((spec, index) => ({
                id: spec.id,
                name: spec.name,
                value: spec.value,
                unit: spec.unit || undefined,
                display_order: index,
                is_primary: spec.is_primary,
            })),
        }

        if (isEditMode) {
            await submitUpdate(payload)
        } else {
            await submitCreate(payload)
        }
    }

    const addSpecification = () => {
        setSpecifications([
            ...specifications,
            {
                tempId: crypto.randomUUID(),
                name: '',
                value: '',
                unit: '',
                is_primary: false,
            },
        ])
    }

    const updateSpecification = (
        tempId: string,
        field: keyof SpecFormData,
        value: string | boolean
    ) => {
        setSpecifications((prev) =>
            prev.map((spec) =>
                spec.tempId === tempId ? { ...spec, [field]: value } : spec
            )
        )
    }

    const removeSpecification = (tempId: string) => {
        setSpecifications((prev) =>
            prev.filter((spec) => spec.tempId !== tempId)
        )
    }

    const categories =
        watchedItemType === 'equipment'
            ? equipmentCategories
            : componentCategories

    if (isEditMode && isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Loading model...</div>
            </div>
        )
    }

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    { path: paths.warehouse, title: 'Capacities & Resources' },
                    { path: paths.warehouseModels, title: 'Item Models' },
                    {
                        path: '',
                        title: isEditMode ? 'Edit Model' : 'New Model',
                    },
                ]}
            />

            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-2 mt-1"
                >
                    {/* Basic Info Section */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Basic Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormItem
                                    control={control}
                                    name="item_type"
                                    label="Item Type *"
                                    type="select"
                                    options={[
                                        {
                                            value: 'equipment',
                                            label: 'Equipment',
                                        },
                                        {
                                            value: 'component',
                                            label: 'Component',
                                        },
                                    ]}
                                    disabled={isEditMode}
                                    rules={{
                                        required: 'Item type is required',
                                    }}
                                />

                                <FormItem
                                    control={control}
                                    name="category"
                                    label="Category *"
                                    type="select"
                                    options={categories}
                                    placeholder="Select category..."
                                    rules={{ required: 'Category is required' }}
                                />

                                <FormItem
                                    control={control}
                                    name="name"
                                    label="Name *"
                                    type="textfield"
                                    placeholder="e.g., Dell PowerEdge R640"
                                    rules={{ required: 'Name is required' }}
                                />

                                <FormItem
                                    control={control}
                                    name="model"
                                    label="Model Code *"
                                    type="textfield"
                                    placeholder="e.g., R640"
                                    rules={{
                                        required: 'Model code is required',
                                    }}
                                />

                                <FormItem
                                    control={control}
                                    name="brand"
                                    label="Brand"
                                    type="textfield"
                                    placeholder="e.g., Dell, HP, Samsung"
                                />

                                <FormItem
                                    control={control}
                                    name="capacity"
                                    label="Capacity"
                                    type="textfield"
                                    placeholder="e.g., 32GB, 1TB, 2U"
                                />

                                <div className="md:col-span-2">
                                    <FormItem
                                        control={control}
                                        name="description"
                                        label="Description"
                                        type="textarea"
                                        placeholder="Optional description..."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Status</Label>
                                    <div className="flex items-center gap-2 h-9">
                                        <Switch
                                            checked={watchedIsActive}
                                            onCheckedChange={(checked) =>
                                                setValue('is_active', checked)
                                            }
                                        />
                                        <span className="text-sm">
                                            {watchedIsActive
                                                ? 'Active'
                                                : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Specifications Section */}
                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <CardTitle>Specifications</CardTitle>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    size="small"
                                    onClick={addSpecification}
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Spec
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {specifications.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    No specifications added yet. Click "Add
                                    Spec" to add one.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {specifications.map((spec) => (
                                        <div
                                            key={spec.tempId}
                                            className="flex items-start gap-3 p-3 bg-muted/30 rounded"
                                        >
                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                                                <div className="space-y-1">
                                                    <Label className="text-xs">
                                                        Name *
                                                    </Label>
                                                    <Input
                                                        value={spec.name}
                                                        onChange={(e) =>
                                                            updateSpecification(
                                                                spec.tempId,
                                                                'name',
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="e.g., CPU"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">
                                                        Value *
                                                    </Label>
                                                    <Input
                                                        value={spec.value}
                                                        onChange={(e) =>
                                                            updateSpecification(
                                                                spec.tempId,
                                                                'value',
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="e.g., Intel Xeon"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">
                                                        Unit
                                                    </Label>
                                                    <Input
                                                        value={spec.unit}
                                                        onChange={(e) =>
                                                            updateSpecification(
                                                                spec.tempId,
                                                                'unit',
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="e.g., GB, MHz"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">
                                                        Primary
                                                    </Label>
                                                    <div className="flex items-center gap-2 h-9">
                                                        <Switch
                                                            checked={
                                                                spec.is_primary
                                                            }
                                                            onCheckedChange={(
                                                                checked
                                                            ) =>
                                                                updateSpecification(
                                                                    spec.tempId,
                                                                    'is_primary',
                                                                    checked
                                                                )
                                                            }
                                                        />
                                                        <span className="text-xs text-muted-foreground">
                                                            {spec.is_primary
                                                                ? 'Yes'
                                                                : 'No'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <Button
                                                type="button"
                                                variant="text"
                                                color="error"
                                                size="small"
                                                onClick={() =>
                                                    removeSpecification(
                                                        spec.tempId
                                                    )
                                                }
                                                className="mt-6"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Form Actions */}
                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="outlined"
                            type="button"
                            onClick={() => navigate(paths.warehouseModels)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            loading={isPending}
                        >
                            {isEditMode ? 'Update Model' : 'Create Model'}
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}

export default ModelForm
